import { createErrorHandler } from "@/app-utils/errors.ts";
import { pushRoute } from "@/app-utils/route.ts";
import { capitalizeFirstLetter, pluralizeWord } from "@/app-utils/string.ts";
import {
  ActivityTypeEnum,
  type InputAddProjectMemberInput,
  type InputRemoveProjectMemberInput,
  type InputUpdateProjectMemberRoleInput,
  type Project,
  type ProjectActivity,
  type ProjectFile,
  type ProjectMember,
  type SavedItem,
  type SavedItemsListing,
  type User,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import { SubscriptionTypeEnum } from "@/modules/notifications/utils/types.ts";
import {
  ACTIVITY_CREATE_COMMENT_FAILURE,
  ACTIVITY_CREATE_COMMENT_SUCCESS,
  ACTIVITY_SUBSCRIPTION_FAILURE,
  ACTIVITY_SUBSCRIPTION_START,
  ACTIVITY_SUBSCRIPTION_STOP,
  ADD_PROJECT_MEMBERS_FAILURE,
  ADD_PROJECT_MEMBERS_SUCCESS,
  CLEAN_PROJECT_ACTIVITIES,
  DELETE_ACTIVITIES_FAILURE,
  DELETE_ACTIVITIES_SUCCESS,
  DELETE_PROJECT_FAILURE,
  DELETE_PROJECT_SUCCESS,
  FETCH_ITEMS_FOR_COMMENT_ATTACHMENTS_FAILURE,
  FETCH_ITEMS_FOR_COMMENT_ATTACHMENTS_REQUEST,
  FETCH_ITEMS_FOR_COMMENT_ATTACHMENTS_SUCCESS,
  LOAD_PROJECT_ACTIVITIES_FAILURE,
  LOAD_PROJECT_ACTIVITIES_SUCCESS,
  LOAD_PROJECT_ACTIVITY_FAILURE,
  LOAD_PROJECT_ACTIVITY_SUCCESS,
  LOAD_PROJECT_FAILURE,
  LOAD_PROJECT_REQUEST,
  LOAD_PROJECT_SUCCESS,
  MODIFY_PROJECT_SUCCESS,
  REMOVE_PROJECT_MEMBER_FAILURE,
  REMOVE_PROJECT_MEMBER_SUCCESS,
  UPDATE_PROJECT_ROLE_FAILURE,
  UPDATE_PROJECT_ROLE_SUCCESS,
} from "@/redux/action-types.ts";
import { PROJECT_ROLE } from "@/settings/settings.ts";
import { webSocketClient } from "@/tools.js";
import { findIndex } from "lodash-es";
import type WebApiClient from "../../api/WebApiClient.js";
import { buildDeleteActivityMessage } from "../../app-utils/alert.js";
import { SelMessage } from "../../messages/SelMessage.js";
import { notify } from "../../modules/notifications/notify.js";
import { type IAppState } from "../reducer.js";
import { type ReduxAction, createReducer } from "../redux-utils.js";
import { getProjectLabel } from "./config.js";

/**
 * State definition
 */
export interface IProjectState {
  project: Project;
  projectActivities: ProjectActivity[];
  isLoading: boolean;
  isLoadingItemsForCommentAttachments: boolean;
  savedItemsForAttachment: SavedItem[];
  projectFilesForAttachment: ProjectFile[];
}

const initialStateSingleProject: IProjectState = {
  project: null,
  projectActivities: [],
  isLoading: false,
  isLoadingItemsForCommentAttachments: false,
  savedItemsForAttachment: [],
  projectFilesForAttachment: [],
};

export interface IUpdateProjectRoleInput extends InputUpdateProjectMemberRoleInput {
  ownUserId: number;
}

/**
 * Reducers
 */
export default createReducer(initialStateSingleProject, {
  [LOAD_PROJECT_REQUEST]: (state: IProjectState): IProjectState => {
    return { ...state, project: null, isLoading: true };
  },
  [LOAD_PROJECT_SUCCESS]: (state: IProjectState, payload: Project): IProjectState => {
    return {
      ...state,
      project: payload,
      isLoading: false,
    };
  },
  [LOAD_PROJECT_FAILURE]: (state: IProjectState): IProjectState => {
    return { ...state, isLoading: false };
  },
  [DELETE_PROJECT_SUCCESS]: (state: IProjectState, payload: number): IProjectState => {
    if (state.project.id === payload) {
      return { ...state, ...initialStateSingleProject };
    }
    return state;
  },
  [LOAD_PROJECT_ACTIVITY_SUCCESS]: (state: IProjectState, newActivity: ProjectActivity): IProjectState => {
    if (!state.projectActivities) {
      // case when the activities have not been loaded yet
      return { ...state, projectActivities: [newActivity] };
    }
    let updatedActivities: ProjectActivity[] = state.projectActivities;
    // only append the activity (payload) if it is not already contained
    if (!updatedActivities.find((projectActivity) => projectActivity.id === newActivity.id)) {
      updatedActivities = state.projectActivities.slice(0);
      updatedActivities.unshift(newActivity);
    }
    return { ...state, projectActivities: updatedActivities };
  },
  [LOAD_PROJECT_ACTIVITIES_SUCCESS]: (state: IProjectState, projectActivities: ProjectActivity[]): IProjectState => {
    return { ...state, projectActivities: projectActivities };
  },
  [CLEAN_PROJECT_ACTIVITIES]: (state: IProjectState): IProjectState => {
    return { ...state, projectActivities: [] };
  },
  [ACTIVITY_CREATE_COMMENT_SUCCESS]: (state: IProjectState, newCommentActivity: ProjectActivity): IProjectState => {
    if (!state.projectActivities) {
      // case when the activities have not been loaded yet
      return { ...state, projectActivities: [newCommentActivity] };
    }
    const updatedActivity: ProjectActivity[] = state.projectActivities.slice(0);
    updatedActivity.unshift(newCommentActivity);
    return { ...state, projectActivities: updatedActivity };
  },
  [DELETE_ACTIVITIES_SUCCESS]: (state: IProjectState, payload) => {
    const activityIds: number[] = payload.activityIds;

    // remove the activity from the state
    const updatedActivity: ProjectActivity[] = state.projectActivities.filter((activity: ProjectActivity) => {
      return !activityIds.includes(activity.id);
    });
    return { ...state, projectActivities: updatedActivity };
  },
  [UPDATE_PROJECT_ROLE_SUCCESS]: (state: IProjectState, payload: IUpdateProjectRoleInput): IProjectState => {
    const updatedProjectMembers: ProjectMember[] = state.project.projectMembers.map((projectMember: ProjectMember) => {
      return projectMember.user.id === payload.userId ? { ...projectMember, roleId: payload.newRoleId } : projectMember;
    });
    // if the user is excluding himself from the admin role of the project, set the global flag as well
    const updatedIsUserAdmin: boolean =
      payload.userId === payload.ownUserId ? isAdminRole(payload.newRoleId) : state.project.isUserAdmin;

    return {
      ...state,
      project: {
        ...state.project,
        projectMembers: updatedProjectMembers,
        isUserAdmin: updatedIsUserAdmin,
      },
    };
  },
  [ADD_PROJECT_MEMBERS_SUCCESS]: (state: IProjectState, payload: ProjectMember[]): IProjectState => {
    const updatedProjectMembers: ProjectMember[] = state.project.projectMembers
      .slice(0)
      .concat(payload)
      .sort((m, n) => {
        if (m.user.username < n.user.username) {
          return -1;
        }
        if (m.user.username > n.user.username) {
          return 1;
        }
        return 0;
      });

    return {
      ...state,
      project: {
        ...state.project,
        projectMembers: updatedProjectMembers,
      },
    };
  },
  [REMOVE_PROJECT_MEMBER_SUCCESS]: (state: IProjectState, payload: InputRemoveProjectMemberInput): IProjectState => {
    const removedUserId: number = payload.userId;
    const userIndex = findIndex(state.project.projectMembers, (m: ProjectMember) => {
      return m.user.id === removedUserId;
    });

    const updatedProjectMembers: ProjectMember[] = state.project.projectMembers.slice(0);
    updatedProjectMembers.splice(userIndex, 1);

    return {
      ...state,
      project: {
        ...state.project,
        projectMembers: updatedProjectMembers,
      },
    };
  },
  [MODIFY_PROJECT_SUCCESS]: (state: IProjectState, payload: Project): IProjectState => {
    return {
      ...state,
      project: {
        ...state.project,
        ...payload,
      },
    };
  },
  [FETCH_ITEMS_FOR_COMMENT_ATTACHMENTS_REQUEST]: (state: IProjectState): IProjectState => {
    return { ...state, isLoadingItemsForCommentAttachments: true };
  },
  [FETCH_ITEMS_FOR_COMMENT_ATTACHMENTS_FAILURE]: (state: IProjectState): IProjectState => {
    return { ...state, isLoadingItemsForCommentAttachments: false };
  },
  [FETCH_ITEMS_FOR_COMMENT_ATTACHMENTS_SUCCESS]: (
    state: IProjectState,
    payload: { savedItems: SavedItemsListing[]; projectFiles: ProjectFile[] },
  ): IProjectState => {
    let listOfSavedItems: SavedItem[] = [];
    listOfSavedItems = Object.keys(payload.savedItems)
      .map((key: string) => payload.savedItems[key])
      .reduce((fullList: SavedItem[], currentList: SavedItem[]) => {
        return fullList.concat(currentList);
      }, listOfSavedItems);
    return {
      ...state,
      savedItemsForAttachment: listOfSavedItems,
      projectFilesForAttachment: payload.projectFiles,
      isLoadingItemsForCommentAttachments: false,
    };
  },
});

/**
 * Action creators
 */

let lastFetchedProjectId = -1;

export function loadProject(projectId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Project> => {
    lastFetchedProjectId = projectId;

    dispatch({ type: LOAD_PROJECT_REQUEST });

    return webApiClient
      .fetchProject(projectId)
      .then((project: Project) => {
        if (lastFetchedProjectId === project.id) {
          dispatch({
            type: LOAD_PROJECT_SUCCESS,
            payload: project,
          });
        }
        return project;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_PROJECT_FAILURE,
          alertTitle: `Fetching of ${getProjectLabel(false, false)} Data Failed`,
        }),
      );
  };
}

export function loadSingleActivity(activityId: number) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    return webApiClient
      .fetchProjectActivity(activityId)
      .then((response: ProjectActivity) => {
        dispatch({
          type: LOAD_PROJECT_ACTIVITY_SUCCESS,
          payload: response,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_PROJECT_ACTIVITY_FAILURE,
          alertTitle: `Fetching of ${getProjectLabel(false, false)} List Failed`,
        }),
      );
  };
}

export function loadProjectActivities(projectId: number) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    if (!projectId || !lastFetchedProjectId) {
      return Promise.resolve(null);
    }
    return webApiClient
      .fetchProjectActivities(projectId)
      .then((response: ProjectActivity[]) => {
        if (lastFetchedProjectId === projectId) {
          dispatch({
            type: LOAD_PROJECT_ACTIVITIES_SUCCESS,
            payload: response,
          });
          return response;
        }
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_PROJECT_ACTIVITIES_FAILURE,
          alertTitle: `Fetching of ${getProjectLabel(false, false)} Activities Failed`,
        }),
      );
  };
}

export function watchProjectActivity(projectId: number): ReduxAction<string> {
  return (dispatch) => {
    const subscriptionId = webSocketClient.subscribe({
      subscription: { eventType: SubscriptionTypeEnum.Project, variables: { projectId } },
      onMessage(message) {
        // success, depending on the type of activity we update specific parts
        try {
          // load activity or also update past activities since some activities potentially impact all the past activities (link, name, access, ..)
          switch (message.activityType) {
            case ActivityTypeEnum.DeletedGroup:
            case ActivityTypeEnum.ModifiedGroup:
            case ActivityTypeEnum.ModifiedView:
            case ActivityTypeEnum.DeletedView:
            case ActivityTypeEnum.DeletedMember:
            case ActivityTypeEnum.DeletedGenomeFlag:
            case ActivityTypeEnum.ModifiedGenomeFlag:
            case ActivityTypeEnum.DeletedProjectFile:
            case ActivityTypeEnum.ModifiedProjectFile:
            case ActivityTypeEnum.DeletedSampleParent:
            case ActivityTypeEnum.ModifiedSampleParent:
              // TODO add more cases here !
              dispatch(loadProjectActivities(projectId)); // since those activities potentially impact all the past activities
              break;
            default:
              dispatch(loadSingleActivity(message.activityId));
          }
        } catch (error) {
          // in case of exception due to NOT_FOUND or UNAUTHORIZED, we don't want to provide a notification to the user,
          // since this is a background update that the user did not request (live update)
        }
      },
      onError: createErrorHandler(dispatch, {
        actionType: ACTIVITY_SUBSCRIPTION_FAILURE,
        showAlert: false, // is going to be handled by the websocket.ts redux module
        throwError: true,
      }),
    });

    dispatch({
      type: ACTIVITY_SUBSCRIPTION_START,
      payload: subscriptionId,
    });

    return subscriptionId;
  };
}

export function unwatchProjectActivity(subscriptionId: string): ReduxAction<void> {
  return (dispatch) => {
    webSocketClient.unsubscribe(subscriptionId);
    dispatch({
      type: ACTIVITY_SUBSCRIPTION_STOP,
    });
  };
}

export function deleteProject(project: Project) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    // dispatch(auditDeleteProject(project));
    return webApiClient
      .deleteProjectAndCascade(project.id)
      .then((_response: boolean) => {
        pushRoute("/home");
        notify.info({
          title: selMessage({
            id: "projects.delete.successTitle",
            values: { projectLabel: getProjectLabel(true) },
          }),
          children: SelMessage({
            id: "projects.delete.successMessage",
            values: {
              projectName: project.name,
            },
          }),
        });
        dispatch({
          type: DELETE_PROJECT_SUCCESS,
          payload: project.id,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_PROJECT_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function postComment(
  projectId: number,
  comment: string,
  groupIds: number[],
  viewIds: number[],
  flagIds: number[],
  projectFileIds: number[],
) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<ProjectActivity> => {
    return webApiClient
      .saveProjectComment(projectId, comment, groupIds, viewIds, flagIds, projectFileIds)
      .then((response: ProjectActivity) => {
        dispatch({
          type: ACTIVITY_CREATE_COMMENT_SUCCESS,
          payload: response,
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: ACTIVITY_CREATE_COMMENT_FAILURE,
          alertTitle: `Comment Creation Failed`,
        }),
      );
  };
}

export function deleteActivities(activityIds: number[], activityType: string) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient
      .deleteActivityList(activityIds)
      .then((response: boolean) => {
        // dispatch(push("/home"));
        dispatch({
          type: DELETE_ACTIVITIES_SUCCESS,
          payload: { activityIds },
        });
        notify.info({
          title: selMessage({
            id: "activities.delete.successTitle",
            values: { activityType: pluralizeWord(capitalizeFirstLetter(activityType), activityIds.length) },
          }),
          children: buildDeleteActivityMessage(activityType, activityIds),
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_ACTIVITIES_FAILURE,
          alertTitle: selMessage({
            id: "activities.delete.errorTitle",
            values: { activityType: capitalizeFirstLetter(activityType) },
          }),
          alertMessage: selMessage({
            id: "activities.delete.errorMessage",
            values: {
              activitiesCount: activityIds.length,
              activityType: pluralizeWord(activityType, activityIds.length),
            },
          }),
        }),
      );
  };
}

export function updateProjectRole(input: IUpdateProjectRoleInput) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    const cleanInput: InputUpdateProjectMemberRoleInput = {
      projectId: input.projectId,
      userId: input.userId,
      newRoleId: input.newRoleId,
    };

    return webApiClient
      .updateProjectRole(cleanInput)
      .then((_response: boolean) => {
        dispatch({
          type: UPDATE_PROJECT_ROLE_SUCCESS,
          payload: input,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: UPDATE_PROJECT_ROLE_FAILURE,
          alertTitle: `Project Member Role Update Failed`,
        }),
      );
  };
}

export function addProjectMembers(input: InputAddProjectMemberInput) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    return webApiClient
      .addProjectMembers(input)
      .then((response: ProjectMember[]) => {
        dispatch({
          type: ADD_PROJECT_MEMBERS_SUCCESS,
          payload: response,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: ADD_PROJECT_MEMBERS_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function removeProjectMember(input: InputRemoveProjectMemberInput) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient) => {
    // if self is removed then push to home (before the removal is actually done otherwise the project activity might be received via websocket and that would trigger requests with 401 response)
    const selfRemoval: boolean = getState().auth.session.id === input.userId;

    return webApiClient
      .removeProjectMember(input)
      .then((_response: boolean) => {
        if (selfRemoval) {
          pushRoute("/home");
        } else {
          dispatch({
            type: REMOVE_PROJECT_MEMBER_SUCCESS,
            payload: input,
          });
        }
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: REMOVE_PROJECT_MEMBER_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function loadItemsForCommentAttachments(projectId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient) => {
    dispatch({ type: FETCH_ITEMS_FOR_COMMENT_ATTACHMENTS_REQUEST });

    return Promise.all([
      webApiClient
        .fetchSavedItemsListing(projectId)
        .then((response: SavedItemsListing[]) => webApiClient.fetchSavedItemsForAttachments(projectId, response)),
      webApiClient.fetchProjectFilesForAttachments(projectId),
    ])
      .then((values: any[]) => {
        const savedItems: SavedItemsListing[] = values[0];
        const projectFiles: ProjectFile[] = values[1];
        dispatch({
          type: FETCH_ITEMS_FOR_COMMENT_ATTACHMENTS_SUCCESS,
          payload: { savedItems, projectFiles },
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: FETCH_ITEMS_FOR_COMMENT_ATTACHMENTS_FAILURE,
          alertTitle: `Failed to Load the Potential Attachments for Commenting`,
        }),
      );
  };
}

/** Utils **/

export function isCurrentUserActivity(activity: ProjectActivity, currentUser: User): boolean {
  return currentUser && activity.user.username === currentUser.username;
}

export function isAdminRole(roleId: number): boolean {
  return roleId === PROJECT_ROLE.ADMIN;
}

export function isManagerRole(roleId: number): boolean {
  return roleId === PROJECT_ROLE.MANAGER;
}
